/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {
    Stack,
    Divider,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    Text,

    /* Hooks */
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import SizeRefinements from './size-refinements'
import CheckboxRefinements from './checkbox-refinements'
import LinkRefinements from '@salesforce/retail-react-app/app/pages/product-list/partials/link-refinements'
import RangeSliderRefinements from './price-range-slider'
import {SIZE_REFINEMENTS_FALLBACK} from '../../../constants'
import {ChevronDownType2} from '../../../components/icons'
import NoFilterMatch from './no-filter-match'
import NoExactMatch from './no-exact-match'

/** Map of refinement attribute IDs to the components used to display values as filter options. */
export const componentMap = {
    cgid: LinkRefinements,
    c_size: SizeRefinements,
    c_refinementColor: CheckboxRefinements,
    price: RangeSliderRefinements
}

const Refinements = ({
    excludedFilters = [],
    filters = [],
    toggleFilter,
    selectedFilters,
    isLoading,
    initiallyExpandedFilters = [],
    sizeGrouping,
    filterRefs,
    productSearchResult,
    category
}) => {
    const refinementsStyles = useMultiStyleConfig('Refinements')
    if (excludedFilters) {
        filters = filters.filter(({attributeId}) => !excludedFilters.includes(attributeId))
    }

    const sizeAttributeIds = sizeGrouping ? sizeGrouping?.split(',') : SIZE_REFINEMENTS_FALLBACK
    const showNoFilterMatch = !isLoading && productSearchResult && !productSearchResult?.hits
    const sizeFilters = filters.filter(
        ({attributeId, values}) =>
            sizeAttributeIds.includes(attributeId) && Array.isArray(values) && values.length > 0
    )
    filters = filters.filter(({attributeId}) => !sizeAttributeIds.includes(attributeId))
    const colourIndex = filters.findIndex((f) => f.attributeId === 'c_refinementColor')

    if (colourIndex >= 0) {
        filters.splice(colourIndex + 1, 0, {
            attributeId: 'c_size',
            label: 'Size',
            values: sizeFilters
        })
    } else {
        filters.push({
            attributeId: 'c_size',
            label: 'Size',
            values: sizeFilters
        })
    }

    // Keep expansion state scoped to the current open action only.
    const filtersIndexes = filters
        ?.map((filter, index) => {
            if (initiallyExpandedFilters.includes(filter.attributeId)) {
                return index
            }
        })
        .filter((index) => index !== undefined)

    const lastFilterIndex = filters.reduce((lastIdx, filter, idx) =>
        filter.values && filter.values.length ? idx : lastIdx, -1)

    return (
        <Stack spacing={8} {...refinementsStyles.container}>
            {showNoFilterMatch ? (
                <NoExactMatch />
            ) : (
                /* Wait to have filters before rendering the Accordion to allow the default indexes to be accurate */
                <Accordion
                    pointerEvents={isLoading ? 'none' : 'auto'}
                    opacity={isLoading ? 0.2 : 1}
                    allowMultiple={true}
                    defaultIndex={filtersIndexes}
                    reduceMotion={false}
                    variant={'filter'}
                >
                    {filters?.map((filter, idx) => {
                        const attributeId =
                            filter.attributeId === 'c_refinementColor' ? 'Colour' : filter.attributeId

                        const Values = componentMap[attributeId] || CheckboxRefinements

                        const sizeId =
                            sizeAttributeIds.find((id) =>
                                Object.prototype.hasOwnProperty.call(selectedFilters, id)
                            ) || 'c_size'

                        let selectedFiltersArray =
                            selectedFilters?.[
                                filter.attributeId === 'c_size' ? sizeId : filter.attributeId
                            ] ?? []

                        if (!Array.isArray(selectedFiltersArray)) {
                            selectedFiltersArray = [selectedFiltersArray]
                        }

                        const sizeValuesFilterExist = filter?.values?.filter(item => item.values && item.values.length > 0)?.length > 0

                        if (filter.values && filter.values.length) {
                            return (
                                <>
                                    <Stack
                                        key={filter.attributeId}
                                        divider={<Divider />}
                                        ref={(el) => {
                                            if (el && filterRefs) {
                                                filterRefs.current[filter.attributeId] = el
                                            }
                                        }}
                                        display={filter?.attributeId === 'c_size' && !sizeValuesFilterExist && 'none'}
                                    >
                                        <AccordionItem
                                            borderTop={idx === 0 ? 'none' : '1px solid webTheme.strokeTertiary'}
                                            borderBottom={idx === lastFilterIndex ? '1px solid webTheme.strokeTertiary' : 'none'}
                                        >
                                            {({isExpanded}) => (
                                                <>
                                                    <AccordionButton
                                                        data-testid={`refinement-accordion-button-${filter.attributeId}`}
                                                        paddingTop={0}
                                                        paddingBottom={isExpanded ? 2 : 0}
                                                    >
                                                        <Text {...refinementsStyles.accTitle}>
                                                            {filter.label}{' '}
                                                            {!!selectedFiltersArray.length && filter.attributeId !== 'price' && (
                                                                <span>({selectedFiltersArray.length})</span>
                                                            )}
                                                        </Text>
                                                        <ChevronDownType2
                                                            transform={
                                                                isExpanded
                                                                    ? 'rotate(180deg)'
                                                                    : 'rotate(0deg)'
                                                            }
                                                            transition="transform 0.2s"
                                                            {...refinementsStyles.accArrow}
                                                        />
                                                    </AccordionButton>
                                                    <AccordionPanel paddingLeft={0}
                                                        {...showNoFilterMatch && {
                                                            paddingBottom: 0
                                                        }}>
                                                        {filter.attributeId === 'c_size' ? (
                                                            <SizeRefinements
                                                                selectedFilters={selectedFiltersArray}
                                                                filter={filter}
                                                                toggleFilter={toggleFilter}
                                                            />
                                                        ) : (
                                                            <Values
                                                                selectedFilters={selectedFiltersArray}
                                                                filter={filter}
                                                                toggleFilter={toggleFilter}
                                                            />
                                                        )}
                                                        {showNoFilterMatch &&
                                                            <NoFilterMatch
                                                                filter={filter}
                                                                toggleFilter={toggleFilter}
                                                                productSearchResult={productSearchResult}
                                                                category={category}
                                                                selectedFilters={selectedFilters}
                                                            />
                                                        }
                                                    </AccordionPanel>
                                                </>
                                            )}
                                        </AccordionItem>
                                    </Stack>
                                    {showNoFilterMatch &&
                                        <Divider />
                                    }
                                </>
                            )
                        } else {
                            return null
                        }
                    })}
                </Accordion>
            )}
        </Stack>
    )
}

Refinements.propTypes = {
    filters: PropTypes.array,
    excludedFilters: PropTypes.arrayOf(PropTypes.string),
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.object,
    isLoading: PropTypes.bool,
    initiallyExpandedFilters: PropTypes.arrayOf(PropTypes.string),
    sizeGrouping: PropTypes.string,
    filterRefs: PropTypes.object,
    productSearchResult: PropTypes.object,
    category: PropTypes.object
}

export default Refinements
